import { useState, useEffect } from 'react'
import { useInView } from 'react-intersection-observer'
import cn from 'clsx'

// Components
import { Image, Section, Pill, CircularProgress } from '@tracklib/kit'

import styles from './quotescarouselblock.css'

type QuotesCarouselBlockProps = {
  value: {
    label?: string
    quotes: Array<{
      text: string
      name: string
      bio: string
      image: number
      image_attribution: string
    }>
  }
}

const QuotesCarouselBlock = ({ value: { label, quotes } }: QuotesCarouselBlockProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState(0)
  const quotesLength = quotes.length

  const [ref, inView] = useInView()

  // set first to active after load to start animation countown
  useEffect(() => {
    if (inView) {
      setActiveIndex(0)
    }
  }, [inView])

  useEffect(() => {
    if (inView) {
      const timeout = setTimeout(() => {
        const newIndex = activeIndex + 1
        setActiveIndex(newIndex > quotesLength - 1 ? 0 : newIndex)
      }, 5000)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [activeIndex, quotesLength, inView])

  return (
    <Section extraPad>
      <div ref={ref} className={styles.wrapper}>
        {quotes.map(({ text, name, bio, image, image_attribution }, index) => (
          <div key={name} className={cn(styles.quote, activeIndex === index && styles.quoteActive)}>
            <div className={styles.media}>
              <Image
                image={image}
                absolute
                className={styles.image}
                sizes="(min-width: 769px) 50vw, 100vw"
                imageWidths={[320, 640, 1024, 1366, 1600]}
                lazy
              />
              <svg viewBox="0 0 100 100" className={styles.curveSvg}>
                <path d="M 0,50 a 50,50 0 1,1 0,1" id="circle" />
                <text className={styles.svgText}>
                  <textPath xlinkHref="#circle" className={styles.svgTextInner}>
                    {image_attribution}
                  </textPath>
                </text>
              </svg>
            </div>
            <div className={styles.content}>
              <div className={styles.variable}>
                {label && <Pill>{label}</Pill>}
                <p className={styles.text}>“{text}”</p>
              </div>
              <p className={styles.name}>{name}</p>
              <p className={styles.bio}>{bio}</p>
            </div>
          </div>
        ))}
        <nav className={styles.nav}>
          {quotes.map((quote, i) => {
            const active = i === activeIndex
            return (
              <button
                key={quote.name}
                onClick={() => setActiveIndex(i)}
                className={cn(styles.navItem, i !== activeIndex && styles.deselected)}
              >
                <div className={styles.navItemImage}>
                  <CircularProgress
                    percentage={active ? 100 : 0}
                    size={72}
                    strokeWidth={2}
                    className={cn(styles.progressBar, active && styles.progressBarActive)}
                  />
                  <Image image={quote.image} absolute sizes="64px" imageWidths={[64, 128, 192]} lazy />
                </div>
                <p className={styles.navItemName} title={quote.name}>
                  {quote.name}
                </p>
              </button>
            )
          })}
        </nav>
      </div>
    </Section>
  )
}

export default QuotesCarouselBlock
